// Placeholder syntax — kept exactly as the mockup wrote it (decision D-W01, docs/dev/DECISIONS.md):
//   {name}            → params.name (numbers get thousands separators: 12,480)
//   {n|one|other}     → "12 others" style plural for English: n formatted, then `one` when n === 1, otherwise `other`
// i18next is configured with prefix '{' / suffix '}' (instead of rewriting 4,4xx strings to {{name}}), a formatter module
// that adds the number grouping, and a missing-interpolation handler that resolves the plural form and leaves anything
// else (unknown placeholder, literal braces in a code sample) untouched — the same output as the mockup's App.t.
import type { Lang } from './resources.js';

export type MessageParams = Readonly<Record<string, unknown>>;

const PLURAL_ALL = /\{(\w+)\|([^|}]*)\|([^}]*)\}/g;
const PLACEHOLDER_ALL = /\{(\w+)\}/g;
const PLURAL_INNER = /^(\w+)\|([^|}]*)\|([^}]*)$/;

export const localeOf = (lang: string | undefined): string => (lang === 'en' ? 'en-US' : 'ko-KR');

/** Integer/number with the screen's grouping (ko-KR and en-US both group with commas). */
export function formatNumberParam(n: number, lang?: string): string {
  return Number(n).toLocaleString(localeOf(lang));
}

function pluralText(raw: unknown, one: string, other: string, lang?: string): string | null {
  if (raw == null) return null;
  const n = Number(raw);
  return `${Number.isFinite(n) ? formatNumberParam(n, lang) : String(raw)} ${n === 1 ? one : other}`;
}

/**
 * The mockup's App.t replacement step as a pure function (no i18next): plural forms first, then {name}.
 * Unknown or null parameters keep their placeholder text.
 */
export function formatMessage(template: string, params?: MessageParams, lang?: Lang): string {
  if (!params) return template;
  return template
    .replace(PLURAL_ALL, (m, k: string, one: string, other: string) => pluralText(params[k], one, other, lang) ?? m)
    .replace(PLACEHOLDER_ALL, (m, k: string) => {
      const v = params[k];
      if (v == null) return m;
      return typeof v === 'number' ? formatNumberParam(v, lang) : String(v);
    });
}

/**
 * i18next `missingInterpolationHandler`: called for every `{…}` that is not a plain parameter.
 * Resolves `{n|one|other}`; returns the original text for everything else (so code samples with braces survive).
 */
export function missingInterpolationHandler(_text: string, match: readonly string[], options?: object): string {
  const whole = match[0] ?? '';
  const inner = (match[1] ?? '').trim();
  const pm = PLURAL_INNER.exec(inner);
  if (!pm || !options) return whole;
  const params = options as Record<string, unknown>;
  const lng = typeof params.lng === 'string' ? params.lng : undefined;
  return pluralText(params[pm[1] as string], pm[2] as string, pm[3] as string, lng) ?? whole;
}

/**
 * i18next formatter module (register with i18next.use(mockupFormatter)). With `alwaysFormat` it sees every parameter:
 * numbers get grouping, null/undefined go to the missing handler (placeholder kept, like the mockup).
 * Format specifiers ({{x, number}}) are not used by the wording, so they are ignored.
 */
export const mockupFormatter = {
  type: 'formatter' as const,
  init(): void {},
  add(): void {},
  addCached(): void {},
  format(value: unknown, _format: string | undefined, lng: string | undefined): unknown {
    if (value == null) return undefined;
    if (typeof value === 'number') return formatNumberParam(value, lng);
    return value;
  },
};

/** Interpolation options for i18next (pair them with mockupFormatter and missingInterpolationHandler). */
export const INTERPOLATION_OPTIONS = {
  prefix: '{',
  suffix: '}',
  // React escapes text; HTML is never built from wording.
  escapeValue: false,
  alwaysFormat: true,
  // The wording never uses i18next format specifiers; a separator that cannot occur keeps "{a, b}" literal.
  formatSeparator: '␞',
  skipOnVariables: true,
} as const;
